import { Suspense, lazy } from 'react';
import type { CodeEditorProps } from './types';
import { DEFAULT_HEIGHT, FALLBACK_LINE_HEIGHT, THEME_BACKGROUNDS, linesToHeight } from './constants';

const LazyCodeEditor = lazy(() => import('./code-editor-impl'));

/**
 * Lazy-loaded Monaco code editor.
 *
 * Monaco (~1 MB gzipped) is split into its own chunk and only downloaded when
 * this component first renders. The fallback is a plain background box that
 * matches the editor theme so the transition is seamless — no spinner flash.
 */
export function CodeEditor(props: CodeEditorProps) {
  const background = THEME_BACKGROUNDS[props.theme ?? 'dark'];
  // An auto-height editor has no `height` to reserve, so the fallback holds
  // the min-line floor, the smallest the editor can settle at. It is the same
  // calculation `useAutoHeight` uses, padding included; reserving anything
  // else makes the box jump once Monaco mounts.
  const height =
    props.autoHeight != null
      ? `${linesToHeight(props.autoHeight.minLines, FALLBACK_LINE_HEIGHT)}px`
      : (props.height ?? DEFAULT_HEIGHT);

  return (
    <Suspense fallback={<div style={{ height, width: '100%', background }} />}>
      <LazyCodeEditor {...props} />
    </Suspense>
  );
}
